import { HttpService, QueryHandler, RootContextBase, TaggedUrl, Tags, transformDTODate } from '@skylab-shared/core';
import { EppState, PurchaseDocumentKind, SaleDocumentKind } from '@skylab-shared/dataModel';
import { Mutable } from '@skylab-shared/types';
import { Transform } from 'class-transformer';
import { CurrencyCollectionQuery } from '../../currency/queries/currency.query-base';
import { PagedCollectionQuery, PagedCollectionQueryResult, PagedCollectionQueryResultDTO } from './../../common/query/paged-collection-query';

export type AccountancyEntriesItem = AccountancyEntriesItemDTO;

class AccountancyEntriesItemDTO {
    public readonly clientName: string;
    public readonly currency: string;
    public readonly precision: number;
    public readonly documentId: number;
    public readonly documentKind: SaleDocumentKind | PurchaseDocumentKind;
    public readonly documentNumber: string;
    public readonly externalDocumentNumber: string;
    public readonly id: number;
    @Transform(transformDTODate, { toClassOnly: true }) public readonly issueDate: Date;
    @Transform(transformDTODate, { toClassOnly: true }) public readonly taxLiabilityDate: Date;
    public readonly state: EppState;
    public readonly totalGrossValue: number;
}

export class AccountancyEntriesQuery extends PagedCollectionQuery<AccountancyEntriesItem> { }

export class AccountancyEntriesQueryHandlerBase implements QueryHandler<AccountancyEntriesQuery, PagedCollectionQueryResult<AccountancyEntriesItem>> {
    public constructor(
        private readonly http: HttpService,
        private readonly rootContext: RootContextBase
    ) { }

    public async handleAsync(query: AccountancyEntriesQuery): Promise<PagedCollectionQueryResult<AccountancyEntriesItem>> {
        const url = TaggedUrl.join('accounting/entries', [Tags.Api]);
        const { data } = await this.http.get<Mutable<PagedCollectionQueryResult<AccountancyEntriesItemDTO>>>(url, {
            params: { ...query.params },
            dtoObject: new PagedCollectionQueryResultDTO<AccountancyEntriesItemDTO>(AccountancyEntriesItemDTO)
        });

        const items = await Promise.all(data.items.map<Promise<AccountancyEntriesItem>>(async item => {
            const currency  = await this.rootContext.firstAsync(CurrencyCollectionQuery, {symbol: item.currency});
            if (!currency) {
                throw new Error(`Currency with symbol ${item.currency} not found`);
            }

            return {
                clientName: item.clientName,
                currency: item.currency,
                documentId: item.documentId,
                documentKind: item.documentKind,
                documentNumber: item.documentNumber,
                externalDocumentNumber: item.externalDocumentNumber,
                id: item.id,
                issueDate: item.issueDate,
                state: item.state,
                taxLiabilityDate: item.taxLiabilityDate,
                totalGrossValue: item.totalGrossValue,
                precision: currency.precision
            };
        }));

        return {
            items: items,
            totalItemsCount: data.totalItemsCount
        };
    }
}
